import { useState, useEffect, useRef, useCallback } from 'react';

type AnimationPhase = 'idle' | 'retracting' | 'streaming';

const ROTATION_INTERVAL_MS = 4000; // Time between status rotations

/**
 * Streaming text animation hook.
 * Provides typewriter-style text animation with variable timing.
 * When text changes, retracts the old text quickly then streams in the new text.
 * Supports array of strings that rotate on a timer.
 */
export function useStreamingText(text: string | string[] | null): {
  displayedText: string;
  isStreaming: boolean;
} {
  const [displayedText, setDisplayedText] = useState('');
  const [phase, setPhase] = useState<AnimationPhase>('idle');
  const [currentIndex, setCurrentIndex] = useState(0);
  const charIndexRef = useRef(0);
  const targetTextRef = useRef<string | null>(null);
  const statusArrayRef = useRef<string[] | null>(null);
  const rotationTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Get current text from array or single string
  const getCurrentText = useCallback((): string | null => {
    if (text === null || text === undefined) return null;
    if (Array.isArray(text)) {
      if (text.length === 0) return null;
      return text[currentIndex % text.length];
    }
    return text;
  }, [text, currentIndex]);

  // Clear rotation timer
  const clearRotationTimer = useCallback(() => {
    if (rotationTimerRef.current) {
      clearInterval(rotationTimerRef.current);
      rotationTimerRef.current = null;
    }
  }, []);

  // Handle text/array changes - set up rotation if array
  useEffect(() => {
    if (text === null || text === undefined) {
      clearRotationTimer();
      statusArrayRef.current = null;
      setCurrentIndex(0);
      setDisplayedText('');
      setPhase('idle');
      charIndexRef.current = 0;
      targetTextRef.current = null;
      return;
    }

    if (Array.isArray(text)) {
      // Check if array content changed
      const prevArray = statusArrayRef.current;
      const arraysEqual =
        prevArray &&
        prevArray.length === text.length &&
        prevArray.every((v, i) => v === text[i]);

      if (!arraysEqual) {
        statusArrayRef.current = text;
        setCurrentIndex(0);

        // Set up rotation timer for arrays with multiple items
        clearRotationTimer();
        if (text.length > 1) {
          rotationTimerRef.current = setInterval(() => {
            setCurrentIndex((prev) => (prev + 1) % text.length);
          }, ROTATION_INTERVAL_MS);
        }
      }
    } else {
      clearRotationTimer();
      statusArrayRef.current = null;
      setCurrentIndex(0);
    }

    return () => clearRotationTimer();
  }, [text, clearRotationTimer]);

  // Handle current text changes (from rotation or new text)
  const currentText = getCurrentText();

  useEffect(() => {
    if (currentText === null) {
      return;
    }

    // Same text - no change needed
    if (currentText === targetTextRef.current) {
      return;
    }

    // New text arrived - store it
    targetTextRef.current = currentText;

    // If we have displayed text, retract it first
    if (displayedText.length > 0 && phase !== 'retracting') {
      setPhase('retracting');
    } else if (displayedText.length === 0 && phase !== 'streaming') {
      // No text to retract, start streaming immediately
      charIndexRef.current = 0;
      setPhase('streaming');
    }
  }, [currentText, displayedText.length, phase]);

  // Transition from retraction to streaming when retraction completes
  useEffect(() => {
    if (phase === 'retracting' && displayedText.length === 0) {
      charIndexRef.current = 0;
      setPhase('streaming');
    }
  }, [phase, displayedText.length]);

  // Retraction animation - fast removal of characters
  useEffect(() => {
    if (phase !== 'retracting') return;

    const retractChunk = () => {
      setDisplayedText((current) => {
        return current.slice(0, Math.max(0, current.length - 5));
      });
    };

    const timer = setTimeout(retractChunk, 15); // Fast retraction
    return () => clearTimeout(timer);
    // displayedText in deps drives the animation loop - each update triggers the next chunk
  }, [phase, displayedText]);

  // Streaming animation
  useEffect(() => {
    if (phase !== 'streaming' || !targetTextRef.current) return;

    const fullText = targetTextRef.current;

    const streamNextChunk = () => {
      if (charIndexRef.current >= fullText.length) {
        setPhase('idle');
        return;
      }

      // Stream 3 characters at a time
      charIndexRef.current = Math.min(
        charIndexRef.current + 3,
        fullText.length,
      );
      setDisplayedText(fullText.slice(0, charIndexRef.current));
    };

    // Variable timing for natural feel
    const currentChar = fullText[charIndexRef.current - 1];
    let delay = 28; // Base speed

    if (currentChar === '!' || currentChar === '?' || currentChar === '.') {
      delay = 120; // Pause after punctuation
    } else if (currentChar === ',') {
      delay = 64; // Brief pause after comma
    } else if (currentChar === ' ') {
      delay = 20; // Faster through spaces
    }

    const timer = setTimeout(streamNextChunk, delay);
    return () => clearTimeout(timer);
    // displayedText in deps drives the animation loop - each update triggers the next chunk
  }, [phase, displayedText]);

  return {
    displayedText,
    isStreaming: phase === 'streaming' || phase === 'retracting',
  };
}
